import { mat3, vec2, vec3 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import {
    DicomDose,
    DoseSummationType,
    DoseType,
    DoseUnit,
} from '~/domain/dicom/dicomDose';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Grid } from '~/engine/data/common/Grid';
import { DoseInputError } from './doseFactor';

export const DERIVED_DOSE_MANUFACTURER = 'MVision Workspace+ (derived dose)';

// The largest stored value used, one below the 32-bit ceiling so rounding cannot wrap.
const STORED_MAX = 2 ** 32 - 2;

export interface QuantizedDose {
    readonly stored: Uint32Array;
    readonly doseGridScalingGy: number;
}

// Stored = round(Gy / scaling); a reading is off by at most half a scaling step.
export function quantizeDoseGy(doseGy: Float32Array): QuantizedDose {
    let maxGy = 0;
    for (let i = 0; i < doseGy.length; i++) {
        const value = doseGy[i];
        if (!Number.isFinite(value) || value < 0) {
            throw new DoseInputError(
                'A dose must be finite and not negative in every voxel.',
                'dose.error.values'
            );
        }
        if (value > maxGy) {
            maxGy = value;
        }
    }
    // An all-zero dose is representable exactly with any scaling.
    const doseGridScalingGy = maxGy === 0 ? 1 : maxGy / STORED_MAX;
    const stored = new Uint32Array(doseGy.length);
    for (let i = 0; i < doseGy.length; i++) {
        const value = Math.round(doseGy[i] / doseGridScalingGy);
        if (value > 2 ** 32 - 1) {
            throw new DoseInputError(
                'A dose value does not fit the stored range.',
                'dose.error.values'
            );
        }
        stored[i] = value;
    }
    return { stored, doseGridScalingGy };
}

export interface DerivedDoseSpec {
    readonly sopInstanceUid: string;
    readonly frameOfReferenceUid: string;
    readonly grid: Grid;
    readonly doseGy: Float32Array;
    readonly description: string;
    // Audit record: algorithm, transform, factors.
    readonly comment: string;
    readonly summationType: DoseSummationType;
    readonly softwareVersion: string;
    readonly referencedStructureSetUid?: string;
}

// A labeled dose computed in this session; it has no plan and is never uploaded or exported.
export function buildDerivedDose(spec: DerivedDoseSpec): DoseMatrix {
    const { grid } = spec;
    const voxels = grid.sizeX * grid.sizeY * grid.sizeZ;
    if (spec.doseGy.length !== voxels) {
        throw new DoseInputError(
            'Dose values do not match the dose grid.',
            'dose.error.grid'
        );
    }
    const { stored, doseGridScalingGy } = quantizeDoseGy(spec.doseGy);
    const m = grid.voxelToWorld;
    const firstVoxelCentre = vec3.transformMat4(
        vec3.create(),
        vec3.fromValues(0.5, 0.5, 0.5),
        m
    );
    const axis = (c: number, res: number) =>
        vec3.fromValues(m[c] / res, m[c + 1] / res, m[c + 2] / res);
    const row = axis(0, grid.resX);
    const column = axis(4, grid.resY);
    const normal = axis(8, grid.resZ);
    const dataset = {
        SOPInstanceUID: spec.sopInstanceUid,
        Modality: 'RTDOSE',
        FrameOfReferenceUID: spec.frameOfReferenceUid,
        Rows: grid.sizeY,
        Columns: grid.sizeX,
        NumberOfFrames: grid.sizeZ,
        BitsAllocated: 32,
        BitsStored: 32,
        HighBit: 31,
        PixelRepresentation: 0,
        DoseUnits: DoseUnit.GY,
        DoseType: DoseType.PHYSICAL,
        DoseSummationType: spec.summationType,
        DoseGridScaling: doseGridScalingGy,
        DoseComment: spec.comment,
        SeriesDescription: spec.description,
        Manufacturer: DERIVED_DOSE_MANUFACTURER,
        PixelData: [stored.buffer],
    };
    const dicomDose = new DicomDose(
        spec.sopInstanceUid,
        spec.frameOfReferenceUid,
        vec2.fromValues(grid.resX, grid.resY),
        grid.resZ,
        firstVoxelCentre,
        mat3.fromValues(
            row[0],
            row[1],
            row[2],
            column[0],
            column[1],
            column[2],
            normal[0],
            normal[1],
            normal[2]
        ),
        spec.description,
        DERIVED_DOSE_MANUFACTURER,
        '',
        grid.sizeY,
        grid.sizeX,
        grid.sizeZ,
        DoseUnit.GY,
        DoseType.PHYSICAL,
        spec.summationType,
        spec.comment,
        doseGridScalingGy,
        spec.softwareVersion,
        '',
        spec.referencedStructureSetUid,
        [],
        new DcmJsDatasetWrapper(dataset)
    );
    return new DoseMatrix(
        dicomDose,
        grid.clone(),
        DoseUnit.GY,
        DoseType.PHYSICAL,
        spec.summationType,
        doseGridScalingGy,
        ''
    );
}

export function isDerivedDose(dose: DoseMatrix): boolean {
    return dose.dicomDose.manufacturer === DERIVED_DOSE_MANUFACTURER;
}
